import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, animate, AnimatePresence } from 'framer-motion';
import { FadeIn } from './ui/FadeIn';
import { Maximize, Minimize, RotateCcw, Move } from 'lucide-react';
const scenes = [{
  id: 'living',
  label: 'Living Room',
  image: 'https://cdn.magicpatterns.com/uploads/eaQPkCXfoAsQQhr9tkfQTT/diamond_city_vr_(18).jpg'
}, {
  id: 'lounge',
  label: 'Lounge',
  image: 'https://cdn.magicpatterns.com/uploads/6B9Vyce6SW7ZRgV3NuokZG/diamond_city_vr_(22).jpg'
}];
export function DemoTour() {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const getBounds = () => {
    if (!viewerRef.current || !innerRef.current) return {
      left: 0,
      right: 0
    };
    const containerWidth = viewerRef.current.offsetWidth;
    const innerWidth = innerRef.current.offsetWidth;
    return {
      left: -(innerWidth - containerWidth),
      right: 0
    };
  };
  const handleDragStart = () => {
    if (!hasInteracted) setHasInteracted(true);
  };
  const handleReset = () => {
    animate(x, 0, {
      duration: 0.6,
      ease: [0.21, 0.47, 0.32, 0.98]
    });
  };
  const handleSceneChange = (idx: number) => {
    if (idx === activeIdx) return;
    setActiveIdx(idx);
    setHasInteracted(true);
    animate(x, 0, {
      duration: 0.4,
      ease: 'easeOut'
    });
  };
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.warn('Fullscreen not supported', err);
    }
  };
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  // Idle hint pan until first interaction
  useEffect(() => {
    if (hasInteracted) return;
    const timer = setTimeout(() => {
      const bounds = getBounds();
      animate(x, [0, bounds.left * 0.15, 0], {
        duration: 6,
        ease: 'easeInOut',
        repeat: Infinity
      });
    }, 1800);
    return () => clearTimeout(timer);
  }, [hasInteracted, activeIdx, x]);
  const activeScene = scenes[activeIdx];
  return <section id="demo" className="py-24 bg-zinc-950" data-id="element-27">
      <div className="max-w-7xl mx-auto px-6" data-id="element-28">
        <FadeIn className="text-center mb-12" data-id="element-29">
          <h2 className="text-3xl md:text-4xl font-display font-medium text-white mb-4" data-id="element-30">
            Experience it yourself
          </h2>
          <p className="text-zinc-400" data-id="element-31">
            Drag to explore. Switch rooms. Step inside a real virtual tour.
          </p>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-32">
          <div ref={containerRef} className="relative rounded-3xl overflow-hidden glass-panel p-2 bg-zinc-950" data-id="element-33">
            <div ref={viewerRef} className="relative aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden bg-zinc-900 cursor-grab active:cursor-grabbing select-none" data-id="element-34">
              {/* Scenes with crossfade */}
              <AnimatePresence mode="sync" data-id="element-35">
                <motion.div key={activeScene.id} ref={innerRef} className="absolute inset-y-0 left-0 h-full flex" style={{
                x,
                width: '260%',
                touchAction: 'pan-y'
              }} drag="x" dragConstraints={getBounds()} dragElastic={0.08} dragMomentum={true} onDragStart={handleDragStart} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                opacity: {
                  duration: 0.5
                }
              }} data-id="element-36">
                  <img src={activeScene.image} alt={`360 degree panorama — ${activeScene.label}`} className="w-full h-full object-cover pointer-events-none" draggable={false} data-id="element-37" />
                </motion.div>
              </AnimatePresence>

              {/* Vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/50 via-transparent to-zinc-950/30 pointer-events-none" data-id="element-38" />

              {/* Top-left scene chip */}
              <div className="absolute top-4 left-4 md:top-6 md:left-6 glass-panel px-4 py-2 rounded-full flex items-center gap-2 pointer-events-none" data-id="element-39">
                <span className="relative flex h-2 w-2" data-id="element-40">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" data-id="element-41"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white" data-id="element-42"></span>
                </span>
                <span className="text-xs font-medium text-white tracking-wide" data-id="element-43">
                  LIVE 360° TOUR
                </span>
              </div>

              {/* Top-right controls */}
              <div className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center gap-2" data-id="element-44">
                <button onClick={handleReset} aria-label="Reset view" className="glass-panel p-2.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors" data-id="element-45">
                  <RotateCcw size={16} data-id="element-46" />
                </button>
                <button onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} className="glass-panel p-2.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors" data-id="element-47">
                  {isFullscreen ? <Minimize size={16} data-id="element-48" /> : <Maximize size={16} data-id="element-49" />}
                </button>
              </div>

              {/* Drag hint - fades after interaction */}
              {!hasInteracted && <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none" initial={{
              opacity: 0
            }} animate={{
              opacity: [0, 1, 1, 0.6]
            }} transition={{
              duration: 2,
              delay: 0.5
            }} data-id="element-50">
                  <motion.div className="glass-panel px-5 py-3 rounded-full flex items-center gap-3 text-white" animate={{
                x: [0, 12, -12, 0]
              }} transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-51">
                    <Move size={18} data-id="element-52" />
                    <span className="text-sm font-medium" data-id="element-53">Drag to explore</span>
                  </motion.div>
                </motion.div>}

              {/* Bottom info */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 glass-panel px-5 py-3 rounded-full flex items-center gap-4 whitespace-nowrap pointer-events-none" data-id="element-54">
                <div className="flex items-center gap-2" data-id="element-55">
                  <div className="w-2 h-2 rounded-full bg-white animate-pulse" data-id="element-56" />
                  <span className="text-sm font-medium text-white" data-id="element-57">
                    {activeScene.label}
                  </span>
                </div>
                <div className="w-px h-4 bg-white/20" data-id="element-58" />
                <span className="text-xs text-zinc-400" data-id="element-59">
                  Scene {activeIdx + 1} of {scenes.length}
                </span>
              </div>
            </div>

            {/* Scene Thumbnails */}
            <div className="flex items-center justify-center gap-3 p-4 md:p-6" data-id="element-60">
              {scenes.map((scene, idx) => <button key={scene.id} onClick={() => handleSceneChange(idx)} className={`group relative rounded-xl overflow-hidden transition-all duration-300 ${idx === activeIdx ? 'ring-2 ring-white scale-105' : 'ring-1 ring-white/10 opacity-60 hover:opacity-100'}`} data-id="element-61">
                  <div className="w-20 h-14 md:w-28 md:h-16 relative" data-id="element-62">
                    <img src={scene.image} alt={scene.label} className="absolute inset-0 w-full h-full object-cover" data-id="element-63" />
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 to-transparent" data-id="element-64" />
                    <span className="absolute bottom-1 left-2 right-2 text-[10px] md:text-xs font-medium text-white truncate text-left" data-id="element-65">
                      {scene.label}
                    </span>
                  </div>
                </button>)}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}